import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Newspaper, 
  Megaphone, 
  Star,
  FileText,
  Calendar,
  User,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'press', name: 'Press Releases', icon: Newspaper },
  { id: 'announcement', name: 'Announcements', icon: Megaphone },
  { id: 'spotlight', name: 'Member Spotlights', icon: Star },
  { id: 'blog', name: 'Blog & Articles', icon: FileText },
];

const sampleArticles = [
  {
    article_id: 'art_1',
    title: 'AREA Welcomes 5,000th Member Milestone',
    slug: 'area-welcomes-5000th-member',
    excerpt: 'The American Real Estate Alliance celebrates a significant growth milestone as membership reaches 5,000 active professionals.',
    category: 'press',
    author_name: 'AREA Communications',
    published_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80',
  },
  {
    article_id: 'art_2',
    title: 'New Buyer Agency Guidelines Now in Effect',
    slug: 'new-buyer-agency-guidelines',
    excerpt: 'Important updates regarding buyer agency agreements and compensation disclosures that all members need to know.',
    category: 'announcement',
    author_name: 'AREA Legal Team',
    published_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&q=80',
  },
  {
    article_id: 'art_3',
    title: 'Member Spotlight: Sarah Johnson\'s Journey to $50M in Sales',
    slug: 'member-spotlight-sarah-johnson',
    excerpt: 'Learn how AREA member Sarah Johnson built her business from scratch to become a top producer in her market.',
    category: 'spotlight',
    author_name: 'AREA Features',
    published_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&q=80',
  },
  {
    article_id: 'art_4',
    title: '5 Strategies for Navigating a Shifting Market',
    slug: '5-strategies-shifting-market',
    excerpt: 'Expert insights on adapting your business strategies in response to changing market conditions.',
    category: 'blog',
    author_name: 'Michael Chen',
    published_at: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1460472178825-e5240623afd5?w=600&q=80',
  },
  {
    article_id: 'art_5',
    title: 'AREA Partners with National Housing Coalition',
    slug: 'area-partners-national-housing',
    excerpt: 'New partnership aims to address affordable housing challenges through collaborative industry efforts.',
    category: 'press',
    author_name: 'AREA Communications',
    published_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&q=80',
  },
  {
    article_id: 'art_6',
    title: 'Annual Conference Registration Now Open',
    slug: 'annual-conference-registration',
    excerpt: 'Register now for the 2024 AREA National Conference in Las Vegas. Early bird pricing available.',
    category: 'announcement',
    author_name: 'AREA Events',
    published_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    image_url: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&q=80',
  },
];

export default function News() {
  const [articles, setArticles] = useState(sampleArticles);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const url = selectedCategory === 'all' 
          ? `${API}/news` 
          : `${API}/news?category=${selectedCategory}`;
        
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          if (data.articles && data.articles.length > 0) {
            setArticles(data.articles);
          }
        }
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [selectedCategory]);

  const filteredArticles = articles.filter(article => {
    return selectedCategory === 'all' || article.category === selectedCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || { name: categoryId, icon: FileText };
  };

  const featuredArticle = filteredArticles[0];
  const otherArticles = filteredArticles.slice(1);

  return (
    <div className="bg-white" data-testid="news-page">
      {/* Hero Section */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl text-center">
          <span className="text-[#D42B3C] font-semibold text-sm tracking-wider uppercase mb-4 block">
            News & Updates
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Stay informed with the latest from AREA
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            The latest news, announcements, and insights from the American Real Estate Alliance.
          </p>
        </div>
      </section>

      {/* News Section */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <Tabs defaultValue="all" onValueChange={setSelectedCategory}>
            <TabsList className="flex flex-wrap justify-center gap-2 mb-12 bg-transparent">
              <TabsTrigger 
                value="all" 
                className="data-[state=active]:bg-[#3D4F7A] data-[state=active]:text-white px-6 py-2 rounded-full"
              >
                All News
              </TabsTrigger>
              {categories.map(category => (
                <TabsTrigger 
                  key={category.id}
                  value={category.id}
                  className="data-[state=active]:bg-[#3D4F7A] data-[state=active]:text-white px-6 py-2 rounded-full"
                >
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={selectedCategory}>
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto"></div>
                </div>
              ) : filteredArticles.length === 0 ? (
                <div className="text-center py-12">
                  <Newspaper className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No articles found in this category.</p>
                </div>
              ) : (
                <>
                  {/* Featured Article */}
                  {featuredArticle && (
                    <Card className="border-0 shadow-xl mb-12 overflow-hidden">
                      <div className="grid grid-cols-1 lg:grid-cols-2">
                        <div className="aspect-[16/10] lg:aspect-auto">
                          <img 
                            src={featuredArticle.image_url} 
                            alt={featuredArticle.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                          <Badge variant="secondary" className="w-fit mb-4 bg-[#D42B3C] text-white">
                            Featured
                          </Badge>
                          <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-slate-900 mb-4">
                            {featuredArticle.title}
                          </h2>
                          <p className="text-slate-600 mb-6">{featuredArticle.excerpt}</p>
                          <div className="flex items-center gap-4 text-sm text-slate-500 mb-6">
                            <span className="flex items-center gap-1">
                              <User className="w-4 h-4" />
                              {featuredArticle.author_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(featuredArticle.published_at)}
                            </span>
                          </div>
                          <Button className="w-fit bg-[#3D4F7A] hover:bg-[#2A3755] text-white">
                            Read More
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </CardContent>
                      </div>
                    </Card>
                  )}

                  {/* Other Articles */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {otherArticles.map((article) => {
                      const categoryInfo = getCategoryInfo(article.category);
                      
                      return (
                        <Card 
                          key={article.article_id} 
                          className="border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
                        >
                          <div className="aspect-[16/10] overflow-hidden">
                            <img 
                              src={article.image_url} 
                              alt={article.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <CardContent className="p-6">
                            <Badge variant="secondary" className="mb-3 bg-[#3D4F7A]/10 text-[#3D4F7A]">
                              {categoryInfo.name}
                            </Badge>
                            <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 line-clamp-2">
                              {article.title}
                            </h3>
                            <p className="text-slate-600 text-sm mb-4 line-clamp-2">{article.excerpt}</p>
                            <div className="flex items-center justify-between text-xs text-slate-500">
                              <span>{article.author_name}</span>
                              <span>{formatDate(article.published_at)}</span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 md:py-28 bg-[#3D4F7A]">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <Newspaper className="w-16 h-16 text-white mx-auto mb-6" />
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
            Stay up to date
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Join AREA to receive exclusive updates, industry insights, and member-only content delivered to your dashboard.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white text-[#3D4F7A] hover:bg-slate-100 px-10 py-6 text-lg font-semibold"
            >
              Join AREA
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
